import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import Input from "../components/Input";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";
import { StarIcon } from "../components/icons";
import { saveMatches } from "../components/PastMatches";
import { useForm } from "../context/FormContext";
import { getTeaserStatus, submitLifeAnswers } from "../services/teaserApi";
import { track } from "../utils/analytics";

// The chart is usually ready in a few seconds. Past this, skip to the preview
// rather than keep someone waiting for questions.
const MAX_WAIT_MS = 25000;
const POLL_MS = 1500;
// Long enough to read as "reading your chart", short enough not to annoy.
const MIN_READING_MS = 4000;

const ORDER = ["marriage", "career", "childbirth", "separation", "difficult"];
// Trait questions ("does this sound like you?") and past-year questions together.
const MAX_QUESTIONS = 4;

// Between questions: show the choice was taken, slide the question out, and
// hold the buttons briefly on the next one. Without this the next question
// appeared instantly, looked like the same one, and got tapped unread.
const CONFIRM_MS = 300;
const LEAVE_MS = 220;
const READ_LOCK_MS = 700;

// Events with a year to give. "difficult" is a stretch, not an event.
const YEAR_LABELS = {
  marriage: "A marriage or committed relationship",
  childbirth: "The birth of a child",
  separation: "A separation or the end of a relationship",
  career: "A new job or a change of direction in work",
};
const MAX_YEARS = 6;

/** Four-digit years from free text like "2009, 2014", oldest first. */
export function parseYears(text, thisYear = new Date().getFullYear()) {
  const years = (text.match(/\d{4}/g) || [])
    .map(Number)
    .filter((y) => y >= 1900 && y <= thisYear);
  return [...new Set(years)].sort((a, b) => a - b).slice(0, MAX_YEARS);
}

/**
 * What to send for each event: its years, [] if it hasn't happened, or
 * nothing if the reader left it blank.
 */
export function eventYearsFrom(entries) {
  const result = {};
  for (const [event, { text, never }] of Object.entries(entries)) {
    if (never) {
      result[event] = [];
    } else {
      const years = parseYears(text || "");
      if (years.length) result[event] = years;
    }
  }
  return result;
}

/** "2004–2006", or "2006" when a window sits inside one year. */
export function yearsLabel(window) {
  const from = window.start.slice(0, 4);
  const to = window.end.slice(0, 4);
  return from === to ? from : `${from}–${to}`;
}

/**
 * The question for one event, in very simple English, as parts so the years
 * and the topic can be shown in bold. Asked about the reader's past, never
 * claimed: the chart picked out these years, and only they know.
 */
export function questionParts(event, windows) {
  const years = windows.map(yearsLabel);
  const yearParts = years.flatMap((y, i) => (i ? [" or ", { bold: y }] : [{ bold: y }]));
  switch (event) {
    case "marriage":
      return ["Did you ", { bold: "marry or start a serious relationship" }, " in ", ...yearParts, "?"];
    case "childbirth":
      return ["Did you ", { bold: "have a child" }, " in ", ...yearParts, "?"];
    case "separation":
      return ["Did a ", { bold: "close relationship end" }, " in ", ...yearParts, "?"];
    case "career":
      return ["Did you ", { bold: "start a new job or change direction in your work" }, " in ", ...yearParts, "?"];
    case "difficult":
      return ["Were ", ...yearParts, " ", { bold: "harder years" }, " for you than most?"];
    default:
      return [];
  }
}

export function questionText(event, windows) {
  return questionParts(event, windows).map((p) => (typeof p === "string" ? p : p.bold)).join("");
}

// The reader's chosen focus decides which past questions come first.
const ORDER_BY_FOCUS = {
  relationships: ["marriage", "separation", "childbirth", "career", "difficult"],
  work: ["career", "difficult", "marriage", "childbirth", "separation"],
};

/**
 * The questions to ask: up to two "does this sound like you?" questions the
 * backend chose from what is unusual in this chart, taking turns with the
 * past-year questions, four at most. A trait goes first: it is the easiest
 * to answer and the most personal.
 */
export function questionsFrom(lifeWindows, focusArea = "") {
  const traits = (lifeWindows?.traits || [])
    .filter((t) => t && t.id && t.question)
    .slice(0, 2)
    .map((t) => ({
      event: t.id,
      kind: "trait",
      text: t.question,
      parts: [t.question],
      basis: t.basis ? t.basis.charAt(0).toUpperCase() + t.basis.slice(1) : "",
    }));
  const past = (ORDER_BY_FOCUS[focusArea] || ORDER)
    .filter((event) => (lifeWindows?.[event] || []).length > 0)
    .slice(0, MAX_QUESTIONS - traits.length)
    .map((event) => ({
      event,
      kind: "past",
      text: questionText(event, lifeWindows[event]),
      parts: questionParts(event, lifeWindows[event]),
    }));
  const mixed = [];
  for (let i = 0; i < Math.max(traits.length, past.length); i += 1) {
    if (traits[i]) mixed.push(traits[i]);
    if (past[i]) mixed.push(past[i]);
  }
  return mixed;
}

/**
 * Questions that no longer make sense after this answer: someone who has
 * never had a serious relationship is not asked whether one ended.
 */
export function questionsToSkip(event, answer) {
  return event === "marriage" && (answer === "no" || answer === "na") ? ["separation"] : [];
}

export default function Questions() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = state?.requestId || searchParams.get("requestId") || "";
  const { formData } = useForm();

  const [questions, setQuestions] = useState(null);
  const [windows, setWindows] = useState({});
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [researchConsent, setResearchConsent] = useState(false);
  // Shown after the last question, only to readers who allowed research.
  const [askingYears, setAskingYears] = useState(false);
  const [yearEntries, setYearEntries] = useState({});
  const [chosen, setChosen] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  function later(ms, fn) {
    timers.current.push(setTimeout(fn, ms));
  }

  function goToPreview() {
    navigate(`/success?requestId=${encodeURIComponent(requestId)}`, {
      replace: true,
      state: { requestId },
    });
  }

  useEffect(() => {
    if (!requestId) {
      navigate("/", { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    const startedAt = Date.now();
    let timer;

    async function poll() {
      let result = null;
      try {
        result = await getTeaserStatus(requestId, controller.signal);
      } catch (err) {
        if (controller.signal.aborted) return;
      }
      const elapsed = Date.now() - startedAt;
      const ready = result && result.lifeWindows;
      const failed = result && result.status === "FAILED";

      if ((ready || failed) && elapsed < MIN_READING_MS) {
        timer = setTimeout(poll, MIN_READING_MS - elapsed);
        return;
      }
      if (ready) {
        const found = questionsFrom(result.lifeWindows, formData.focusArea);
        if (found.length === 0) {
          goToPreview();
        } else {
          track("life_questions_view", { requestId });
          setWindows(result.lifeWindows);
          setQuestions(found);
        }
        return;
      }
      if (failed) {
        goToPreview();
        return;
      }
      if (elapsed >= MAX_WAIT_MS) {
        // The windows may still arrive; tell the backend not to wait for answers.
        skipToPreview({});
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    }

    poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  function answer(value) {
    if (locked) return;
    const { event } = questions[index];
    const next = { ...answers, [event]: value };
    setAnswers(next);
    track("life_question_answered", { requestId, question: event, answer: value });
    setChosen(value);
    setLocked(true);
    // Drop later questions this answer rules out.
    const skip = questionsToSkip(event, value);
    const remaining = questions.filter((q, i) => i <= index || !skip.includes(q.event));

    later(CONFIRM_MS, () => setLeaving(true));
    later(CONFIRM_MS + LEAVE_MS, () => {
      setChosen(null);
      setLeaving(false);
      if (remaining.length !== questions.length) setQuestions(remaining);
      if (index + 1 < remaining.length) {
        setIndex(index + 1);
        later(READ_LOCK_MS, () => setLocked(false));
        return;
      }
      setLocked(false);
      if (researchConsent && questions.some((q) => YEAR_LABELS[q.event])) {
        setAskingYears(true);
        return;
      }
      finish(next, {});
    });
  }

  function finish(finalAnswers, eventYears) {
    saveMatches(requestId, finalAnswers, windows);
    // Saving must never stand between someone and their preview.
    submitLifeAnswers(requestId, finalAnswers, researchConsent, eventYears).catch(() => {});
    goToPreview();
  }

  // The preview waits for answers before it is written, so a skip is sent
  // too (with any answers given so far) to let it start straight away.
  function skipToPreview(soFar = answers) {
    finish(soFar, {});
  }

  function updateYears(event, change) {
    setYearEntries({ ...yearEntries, [event]: { ...yearEntries[event], ...change } });
  }

  return (
    <Background>
      <div className="min-h-screen px-4 py-10 flex flex-col items-center">
        <div className="w-full max-w-md mx-auto flex-1">
          <AyupathCard>
            <div className="space-y-6">
              <div className="text-center space-y-3">
                <div className="mx-auto h-14 w-14 rounded-3xl bg-orange-100 grid place-items-center text-orange-600">
                  <StarIcon />
                </div>
                {askingYears ? (
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-900">One optional step</h2>
                    <p className="text-gray-600">
                      To test the chart fairly we need the years these actually happened, including
                      years outside the stretches above. Leave anything blank you'd rather not share.
                    </p>
                  </div>
                ) : questions ? (
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-900">Before your preview</h2>
                    <p className="text-gray-600">
                      {questions.some((q) => q.kind === "trait")
                        ? "A few questions from your chart. Do they fit you?"
                        : "Your chart points to a few stretches of your past. Do they match?"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold text-gray-900">Reading your chart</h2>
                    <p className="text-gray-600" role="status" aria-live="polite">
                      Working through your planetary periods…
                    </p>
                  </div>
                )}
              </div>

              {!questions ? (
                <div className="flex justify-center gap-1.5" aria-hidden="true">
                  <span className="h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
                  <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse [animation-delay:150ms]" />
                  <span className="h-2 w-2 rounded-full bg-orange-600 animate-pulse [animation-delay:300ms]" />
                </div>
              ) : askingYears ? (
                <div className="space-y-5">
                  {questions
                    .filter((q) => YEAR_LABELS[q.event])
                    .map(({ event }) => {
                      const entry = yearEntries[event] || {};
                      return (
                        <fieldset key={event} className="space-y-2">
                          <legend className="text-sm font-medium text-gray-900">
                            {YEAR_LABELS[event]}
                          </legend>
                          <Input
                            type="text"
                            inputMode="numeric"
                            aria-label={`${YEAR_LABELS[event]}: years`}
                            placeholder="Year or years, e.g. 2009, 2014"
                            value={entry.text || ""}
                            disabled={entry.never}
                            onChange={(e) => updateYears(event, { text: e.target.value })}
                          />
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={entry.never || false}
                              onChange={(e) => updateYears(event, { never: e.target.checked })}
                              className="h-4 w-4 accent-orange-600"
                            />
                            Hasn't happened
                          </label>
                        </fieldset>
                      );
                    })}
                  <PrimaryButton onClick={() => finish(answers, eventYearsFrom(yearEntries))}>
                    See my preview
                  </PrimaryButton>
                  <div className="text-center">
                    <SecondaryButton onClick={() => finish(answers, {})}>Skip this step</SecondaryButton>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 text-center" aria-live="polite">
                      Question {index + 1} of {questions.length}
                    </p>
                    <div className="flex justify-center gap-1.5" aria-hidden="true">
                      {questions.map((q, i) => (
                        <span key={q.event}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            i < index ? "w-6 bg-orange-500" : i === index ? "w-10 bg-orange-500" : "w-6 bg-gray-200"
                          }`} />
                      ))}
                    </div>
                  </div>
                  <div
                    key={index}
                    className={`bg-orange-50 rounded-2xl p-5 motion-safe:animate-question-in transition duration-200 ${
                      leaving ? "opacity-0 motion-safe:-translate-x-6" : "opacity-100"
                    }`}
                  >
                    {questions[index].basis ? (
                      <p className="text-xs font-medium text-orange-700 mb-2">
                        From your chart: {questions[index].basis}.
                      </p>
                    ) : null}
                    <p className="text-lg text-gray-900 leading-relaxed">
                      {questions[index].parts.map((part, i) => (typeof part === "string"
                        ? <span key={i}>{part}</span>
                        : <strong key={i} className="font-bold text-gray-900">{part.bold}</strong>))}
                    </p>
                  </div>
                  {questions[index].kind === "trait" ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <AnswerButton value="yes" chosen={chosen} locked={locked} onAnswer={answer}>Yes</AnswerButton>
                        <AnswerButton value="no" chosen={chosen} locked={locked} onAnswer={answer}>No</AnswerButton>
                      </div>
                      <AnswerButton value="sometimes" secondary chosen={chosen} locked={locked} onAnswer={answer}>
                        Sometimes
                      </AnswerButton>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <AnswerButton value="yes" chosen={chosen} locked={locked} onAnswer={answer}>Yes</AnswerButton>
                        <AnswerButton value="no" chosen={chosen} locked={locked} onAnswer={answer}>No</AnswerButton>
                      </div>
                      {/* Yes/No alone forced a "No" from people whose event came in another year,
                          or who were asked about something that never applied to them. */}
                      <div className="grid grid-cols-2 gap-3">
                        <AnswerButton value="other_year" secondary chosen={chosen} locked={locked} onAnswer={answer}>
                          Yes, but in other years
                        </AnswerButton>
                        <AnswerButton value="na" secondary chosen={chosen} locked={locked} onAnswer={answer}>
                          Doesn't apply to me
                        </AnswerButton>
                      </div>
                    </div>
                  )}
                  <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={researchConsent}
                      onChange={(event) => setResearchConsent(event.target.checked)}
                      className="h-5 w-5 shrink-0 accent-orange-600"
                    />
                    Use my answers, without my name or email, to help AyuPath test and improve its accuracy.
                  </label>
                  <div className="text-center">
                    <SecondaryButton onClick={() => skipToPreview()}>Skip to my preview</SecondaryButton>
                  </div>
                </div>
              )}
            </div>
          </AyupathCard>
        </div>
      </div>
    </Background>
  );
}

/**
 * Yes/No that shows the tap was taken: the chosen one gets a tick, the other
 * fades, and both ignore taps until the next question has settled. Stays
 * orange while locked, since a grey button reads as broken.
 */
function AnswerButton({ value, chosen, locked, onAnswer, secondary = false, children }) {
  const picked = chosen === value;
  const look = secondary
    ? "py-3 text-sm font-medium text-gray-800 bg-white border border-gray-300"
    : "py-4 font-semibold tracking-wide text-white bg-gradient-to-r from-orange-500 to-orange-600";
  return (
    <button
      type="button"
      aria-disabled={locked}
      onClick={() => onAnswer(value)}
      className={`w-full rounded-2xl transition duration-200 ${look} ${
        picked ? "ring-4 ring-orange-200 scale-[0.98]" : chosen ? "opacity-40" : locked ? "opacity-70" : "hover:brightness-110 active:brightness-95"
      } ${locked ? "cursor-default" : ""}`}
    >
      {children}{picked ? " ✓" : ""}
    </button>
  );
}
